import {DataTableColumn} from "mantine-datatable";
import {ProductFeature} from "../types.ts";
import {I18n} from "@aws-amplify/core";
import {formatTimestamp, TIMESTAMP_PLACEHOLDER} from "../../../utils/TableTimestamps.tsx";
import {
    Button,
    Group
} from "@mantine/core";
import {useQueryClient} from "@tanstack/react-query";
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {
    disableRecord,
    enableRecord
} from "../services/ProductFeaturesService.ts";
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {StatusBadge} from '../profile/StatusBadge.tsx';
import {productFeaturesQueryKeys} from './useProductFeatures.ts';

// Every data column is sortable (§3a). The `accessor` is what sorts: `created` and
// `updated` sort as ISO-8601 strings, nulls last (shared sortRecords). Empty values render
// the shared placeholder (§13). The Actions column is not data and does not sort.
export const useProductFeaturesTableColumns = () => {
    const queryClient = useQueryClient();

    // Enable / Disable change the feature, so they need UpdateProductFeature, like the
    // row-click edit does.
    const {allowedActions} = useVerifiedPermissions();
    const canUpdate = allowedActions.includes(rbacActions.UpdateProductFeature);

    const handleEnable = async (id: string) => {
        try {
            await enableRecord(id);
            notifySuccess(I18n.get('Product Feature enabled'));
            queryClient.invalidateQueries({queryKey: productFeaturesQueryKeys.all});
        } catch (e) {
            notifyError(e);
        }
    };

    const handleDisable = async (id: string) => {
        try {
            await disableRecord(id);
            notifySuccess(I18n.get('Product Feature disabled'));
            queryClient.invalidateQueries({queryKey: productFeaturesQueryKeys.all});
        } catch (e) {
            notifyError(e);
        }
    };

    const columns: DataTableColumn<ProductFeature>[] = [
        {
            accessor: 'name',
            title: I18n.get('Name'),
            width: '1fr',
            sortable: true,
        },
        {
            accessor: 'category',
            title: I18n.get('Category'),
            width: '1fr',
            sortable: true,
            render: (item: ProductFeature) => item.category || TIMESTAMP_PLACEHOLDER,
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            width: '120px',
            sortable: true,
            render: (item: ProductFeature) => <StatusBadge status={item.status}/>,
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            width: '1fr',
            sortable: true,
            render: (item: ProductFeature) => formatTimestamp(item.created),
        },
        {
            accessor: 'updated',
            title: I18n.get('Last Updated'),
            width: '1fr',
            sortable: true,
            render: (item: ProductFeature) => formatTimestamp(item.updated),
        },
    ];

    if (canUpdate) {
        columns.push({
            accessor: 'actions',
            title: I18n.get('Actions'),
            width: '150px',
            // The buttons sit inside a clickable row; stop the click opening the edit drawer.
            render: (item: ProductFeature) => (
                <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                    {(!item.status || item.status === 'Inactive' || item.status === 'Draft') && (
                        <Button size="xs" onClick={() => handleEnable(item.id)}>
                            {I18n.get('Enable')}
                        </Button>
                    )}
                    {item.status === 'Active' && (
                        <Button size="xs" color="red" onClick={() => handleDisable(item.id)}>
                            {I18n.get('Disable')}
                        </Button>
                    )}
                </Group>
            ),
        });
    }

    return {
        columns,
    }
}
